import { Fade, Grid, IconButton, ListItem, Menu, MenuItem, Paper, Tooltip, Typography, useTheme } from "@mui/material";
import { alpha, lighten } from "@mui/material/styles";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import debounce from "lodash/debounce";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useBrowsingContext } from "../../../context/BrowsingContext";
import { Attachment, InputBoxProps } from "../../../types/chatTypes";
import { streamStop } from "../../../utils/stream_stop";
import { ColumnCenteredBox, RowBox } from "../../../utils/styledBox";
import { AttachmentInput } from "./AttachmentInput";
import { MessageInput } from "./MessageInput";
import { Toolbar } from "./Toolbar";
import { VoiceInput } from "./VoiceInput";
import BoltIcon from "@mui/icons-material/Bolt";
import AssistantIcon from "@mui/icons-material/Assistant";

function InputBox(props: InputBoxProps) {
  const { sendMessage, historyData, stopStreamController, historyRef, onModeChange, mode, aiMode, setAiMode } = props;
  const { chatUuid } = useParams();
  const { processing, inputQuery, setInputQuery } = useBrowsingContext();
  const isProcessing = processing[chatUuid!];
  // ローカルステートで入力を管理
  const [localInputQuery, setLocalInputQuery] = useState(inputQuery[chatUuid!] || "");
  const [inputError, setInputError] = useState(false);
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [isRecording, setIsRecording] = useState(false);
  const { t } = useTranslation();
  const mainContainer = document.getElementById("main");

  // 最下部のフッターと重ならないようにリッスン
  const [atBottom, setAtBottom] = useState(true);
  const handleScroll = () => {
    if (mainContainer) {
      // mainコンテナのスクロール位置をチェック
      const isAtBottom = mainContainer.scrollHeight - (mainContainer.scrollTop + mainContainer.clientHeight) < 100;
      setAtBottom(isAtBottom);
    }
  };

  useEffect(() => {
    if (mainContainer) {
      // mainコンテナのスクロールイベントをリッスン
      mainContainer.addEventListener("scroll", handleScroll);
      return () => mainContainer.removeEventListener("scroll", handleScroll);
    }
  }, [chatUuid]);

  // debounceされた自動スクロール関数
  const debouncedScrollToBottom = useCallback(
    debounce((wait = 0) => {
      if (mainContainer) {
        setTimeout(() => {
          mainContainer.scrollTo({
            top: mainContainer.scrollHeight,
            behavior: "smooth",
          });
        }, wait);
      }
    }, 100), // 100msのデバウンス時間
    [mainContainer]
  );

  // chatResponseが変更されるたびに実行されるuseEffect
  useEffect(() => {
    // スクロール位置が最下部に近い場合は自動スクロール
    if (atBottom) {
      debouncedScrollToBottom(0);
    }
  }, [mainContainer?.scrollHeight, atBottom, debouncedScrollToBottom]);

  // 履歴データが変更された場合にも自動スクロール適用
  useEffect(() => {
    if (historyData.length > 0) {
      debouncedScrollToBottom(0);
    }
  }, [historyData.length, debouncedScrollToBottom]);

  // Chatが変更されたら最下部にスクロール
  useEffect(() => {
    debouncedScrollToBottom(50);
  }, [chatUuid, debouncedScrollToBottom]);

  // Fixed Box のメッセージ送信用の横幅を取得
  const elementRef = useRef<HTMLDivElement | null>(null);
  const [width, setWidth] = useState<number | undefined>(undefined);

  useEffect(() => {
    const updateWidth = () => {
      if (historyRef?.current) {
        setWidth(historyRef.current.offsetWidth);
      }
    };

    const debouncedUpdateWidth = debounce(updateWidth, 0);

    updateWidth();

    const resizeObserver = new ResizeObserver(debouncedUpdateWidth);
    if (historyRef?.current) {
      resizeObserver.observe(historyRef.current);
    }

    return () => {
      if (historyRef?.current) {
        resizeObserver.unobserve(historyRef.current);
      }
    };
  }, [historyRef]);

  // ユーザーの入力バリデーション
  const handleInputQueryChange = (value: string) => {
    setLocalInputQuery(value);
    if (value.length > 6000) {
      setInputError(true);
    } else {
      setInputError(false);
    }
  };

  const handleAttachmentAdd = useCallback(
    (files: FileList | null, type: "file" | "image") => {
      if (!files) return;

      const validFiles = Array.from(files).filter((file) => {
        if (file.size > 20 * 1024 * 1024) {
          alert(`${file.name}は20MB以上のため、アップロードできません。`);
          return false;
        }
        return true;
      });

      if (attachments.length + validFiles.length > 4) {
        alert("uploaded files exceed the limit of 4.");
        return;
      }

      const uploadedFiles: Attachment[] = validFiles.map((file) => ({
        type,
        url: URL.createObjectURL(file),
        file,
      }));

      setAttachments((prev) => [...prev, ...uploadedFiles]);
    },
    [attachments.length]
  );

  const handleAttachmentRemove = useCallback((index: number) => {
    setAttachments((prev) => {
      const removedAttachment = prev[index];
      if (removedAttachment) {
        URL.revokeObjectURL(removedAttachment.url);
      }
      return prev.filter((_, i) => i !== index);
    });
  }, []);

  const handleImagePaste = useCallback(
    (files: FileList) => {
      handleAttachmentAdd(files, "image");
    },
    [handleAttachmentAdd]
  );

  // コンポーネントのアンマウント時にURLを解放
  useEffect(() => {
    return () => {
      attachments.forEach((attachment) => {
        URL.revokeObjectURL(attachment.url);
      });
    };
  }, [attachments]);

  const send = () => {
    const images = ["llm", "image"].includes(mode)
      ? attachments.filter((a) => a.type === "image").map((a) => a.file)
      : [];
    const files = attachments.filter((a) => a.type === "file").map((a) => a.file);

    // コンテキストにローカルステートの値を反映してから送信
    setInputQuery(chatUuid!, localInputQuery);
    // フロントエンド改修のみなので、既存のsendMessage関数をそのまま使用
    sendMessage(localInputQuery, images, files);
    setLocalInputQuery("");
    setAttachments([]);
  };

  const handleStreamStop = () => {
    stopStreamController();
    streamStop(chatUuid!);
  };

  const theme = useTheme();

  const historyExists = historyData.length > 0;
  const bgcolor = () => {
    if (!historyExists) return "transparent";
    return alpha(lighten(theme.palette.background.paper, 0.08), 0.9);
  };

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleAiModeChange = (newMode: "SPEED" | "BALANCE" | "INTELLIGENCE") => {
    setAiMode(newMode);
    handleMenuClose();
  };

  // 現在のモードに応じたアイコンを選択
  const getCurrentModeIcon = () => {
    switch (aiMode) {
      case "SPEED":
        return <BoltIcon />;
      case "BALANCE":
        return <AssistantIcon />;
      case "INTELLIGENCE":
        return <SmartToyIcon />;
    }
  };

  return (
    <ColumnCenteredBox sx={{ width: "inherit" }}>
      <Fade in={true} key={1} timeout={1500}>
        <Paper
          elevation={0}
          ref={elementRef}
          sx={{
            width: { xs: "100%", sm: width },
            mb: { xs: 0, sm: 2 },
            borderRadius: 1,
            position: "fixed",
            bottom: 0,
            backgroundColor: bgcolor,
            backdropFilter: "blur(5px)",
            zIndex: 100,
            px: { xs: 2, sm: 3 },
            pt: 2,
            pb: { xs: 1, sm: 1.5 },
            transition: "all 0.3s ease",
            left: { xs: 0, sm: "50%" },
            transform: { xs: "translateX(0)", sm: "translateX(-50%)" },
          }}
        >
          <Grid container spacing={1} sx={{}}>
            <Grid size={12}>
              <MessageInput
                value={localInputQuery}
                onChange={handleInputQueryChange}
                onSend={send}
                onStop={handleStreamStop}
                processing={isProcessing}
                error={inputError}
                onPaste={handleImagePaste}
              />
            </Grid>
            <Grid size={1.5}>
              <RowBox>
                <Tooltip title={t("browsing.aiMode.aiModeTooltip")}>
                  <span>
                    <IconButton aria-label={t("browsing.aiMode.aiMode")} onClick={handleMenuClick} size="small">
                      {getCurrentModeIcon()}
                    </IconButton>
                  </span>
                </Tooltip>
                <Menu
                  anchorEl={anchorEl}
                  open={open}
                  onClose={handleMenuClose}
                  anchorOrigin={{
                    vertical: "top",
                    horizontal: "center",
                  }}
                  transformOrigin={{
                    vertical: "bottom",
                    horizontal: "center",
                  }}
                >
                  <ListItem>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        fontWeight: "bold",
                        fontSize: "0.9rem",
                        color: theme.palette.text.primary,
                      }}
                    >
                      AI MODEL
                    </Typography>
                  </ListItem>
                  <MenuItem
                    onClick={() => handleAiModeChange("SPEED")}
                    selected={aiMode === "SPEED"}
                    sx={{ minWidth: "150px" }}
                  >
                    <BoltIcon fontSize="small" sx={{ mr: 1 }} />
                    {t("browsing.aiMode.speed")}
                  </MenuItem>
                  <MenuItem
                    onClick={() => handleAiModeChange("BALANCE")}
                    selected={aiMode === "BALANCE"}
                    sx={{ minWidth: "150px" }}
                  >
                    <AssistantIcon fontSize="small" sx={{ mr: 1 }} />
                    {t("browsing.aiMode.balance")}
                  </MenuItem>
                  <MenuItem
                    onClick={() => handleAiModeChange("INTELLIGENCE")}
                    selected={aiMode === "INTELLIGENCE"}
                    sx={{ minWidth: "150px" }}
                  >
                    <SmartToyIcon fontSize="small" sx={{ mr: 1 }} />
                    {t("browsing.aiMode.intelligent")}
                  </MenuItem>
                </Menu>

                <AttachmentInput
                  mode={mode}
                  attachments={attachments}
                  onAttachmentAdd={handleAttachmentAdd}
                  onAttachmentRemove={handleAttachmentRemove}
                />
              </RowBox>
            </Grid>

            <Grid size={10}>
              <Toolbar mode={mode} onModeChange={onModeChange} />
            </Grid>

            <Grid sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end" }} size={{ xs: 0.5 }}>
              <VoiceInput
                isRecording={isRecording}
                onVoiceInput={() => setIsRecording(!isRecording)}
                onTranscriptChange={handleInputQueryChange}
                initialText={localInputQuery}
              />
            </Grid>
          </Grid>
        </Paper>
      </Fade>
    </ColumnCenteredBox>
  );
}

export default InputBox;
